import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Modal, Select, Table, Tag, Alert, Input, Typography, Button, message } from '../../ui';
import { request } from '../../api/client';
import { fmtQty } from '../../lib/format';
import { useAnyPermission } from '../../store/auth';

const inr2 = (v: any) => `₹${Number(v ?? 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * Approve an order AND assign its fulfillment (dispatch) warehouse in one step. The chosen
 * warehouse's manager then packs + dispatches it. Shows live per-warehouse availability against the
 * order's lines; if the warehouse is short, the admin can still approve (the shortfall backorders)
 * or "Approve & create transfers" to consolidate stock into that warehouse first.
 */
export function ApproveOrderModal({ open, onClose, orderId, orderNo, defaultWarehouseId, onApproved }: {
  open: boolean;
  onClose: () => void;
  orderId: string | null;
  orderNo?: string | null;
  defaultWarehouseId?: string | null;
  onApproved?: () => void;
}) {
  const qc = useQueryClient();
  // Creating transfers (consolidation) is an ops action — only offer it to approvers who can do it.
  const canConsolidate = useAnyPermission(['ORDER.DISPATCH.PLAN', 'ORDER.EDIT']);
  const [wid, setWid] = useState<string | undefined>();
  const [notes, setNotes] = useState('');

  // Prefill the warehouse on open: fresh approvals start empty; a re-approval (e.g. BACKORDER→APPROVED)
  // starts on the order's existing fulfillment warehouse so it isn't silently reassigned.
  useEffect(() => {
    if (open) { setWid(defaultWarehouseId ? String(defaultWarehouseId) : undefined); setNotes(''); }
  }, [open, defaultWarehouseId]);

  const { data: warehouses } = useQuery({
    queryKey: ['warehouses'],
    queryFn: () => request<any[]>({ url: '/warehouses' }),
    enabled: open,
  });
  const { data: plan, isFetching: planLoading } = useQuery({
    queryKey: ['order-stock-plan', orderId, wid],
    queryFn: () => request<any>({ url: `/orders/${orderId}/stock-plan`, params: wid ? { warehouseId: wid } : {} }),
    enabled: open && !!orderId && !!wid,
  });
  // Prepaid / advance context — surfaced so the approver knows money is committed (prioritise fulfilment)
  // and whether an advance payment still needs verifying. Read-only; does NOT gate approval.
  const { data: advanceCd } = useQuery({
    queryKey: ['advance-cd', orderId],
    queryFn: () => request<any>({ url: `/orders/${orderId}/advance-cd` }),
    enabled: open && !!orderId,
  });
  const { data: orderPayments } = useQuery({
    queryKey: ['order-payments', orderId],
    queryFn: () => request<any[]>({ url: `/orders/${orderId}/payments` }),
    enabled: open && !!orderId,
  });
  const advPaid = (orderPayments ?? []).filter((p: any) => p.approvalStatus === 'APPROVED' && p.status !== 'REFUNDED').reduce((s: number, p: any) => s + Number(p.amount || 0), 0);
  const advPending = (orderPayments ?? []).filter((p: any) => p.approvalStatus === 'PENDING').reduce((s: number, p: any) => s + Number(p.amount || 0), 0);
  const cdStatus: string | undefined = advanceCd?.status;
  const isPrepaid = cdStatus === 'ESTIMATED' || cdStatus === 'LOCKED' || advPaid > 0 || advPending > 0;

  const close = () => { setWid(undefined); setNotes(''); onClose(); };

  const approve = useMutation({
    mutationFn: async (opts: { consolidate?: boolean }) => {
      await request({ url: `/orders/${orderId}/approve`, method: 'POST', data: { warehouseId: wid, notes: notes.trim() || undefined } });
      // Approve has committed. Consolidation is a best-effort SECOND step — caught so a transfer
      // failure never re-runs /approve (which would dead-end on "APPROVED→APPROVED").
      let consolidateError: string | null = null;
      if (opts.consolidate) {
        try {
          await request({ url: `/orders/${orderId}/consolidate`, method: 'POST', data: { toWarehouseId: wid } });
        } catch (e: any) {
          consolidateError = e?.response?.data?.error?.message ?? 'creating transfers failed';
        }
      }
      return { consolidateError };
    },
    onSuccess: (res: { consolidateError: string | null }) => {
      if (res.consolidateError) {
        message.warning(`Order approved — but creating transfers failed: ${res.consolidateError}. Use the order's Stock Planning to consolidate.`);
      } else {
        message.success('Order approved & warehouse assigned');
      }
      qc.invalidateQueries({ queryKey: ['orders'] });
      qc.invalidateQueries({ queryKey: ['orders-pending-approval'] });
      qc.invalidateQueries({ queryKey: ['order', orderId] });
      qc.invalidateQueries({ queryKey: ['badges'] });
      onApproved?.();
      close();
    },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Approval failed'),
  });

  const lines: any[] = plan?.lines ?? [];
  const short = !!wid && !!plan && !plan.fullyAvailableHere;
  const totalShort = lines.some((l) => Number(l.availableTotal ?? 0) < Number(l.pendingQty ?? 0)); // even all-WH can't cover
  // Advisory (no reservation): some physical stock here is already spoken-for by other active orders.
  const committedHere = Number(plan?.committedHereTotal ?? 0) > 0;
  const freeCovers = !!plan?.freeCoversHere;

  const whOptions = (warehouses ?? []).map((w: any) => ({
    value: String(w.id),
    text: `${w.code} · ${w.name}`,
    label: `${w.code} · ${w.name}`,
    isMaster: !!w.isMaster,
  }));

  return (
    <Modal
      open={open}
      onOpenChange={(o) => { if (!o) close(); }}
      title={`Approve order${orderNo ? ` · ${orderNo}` : ''}`}
      width={700}
      footer={[
        <Button key="cancel" variant="default" onClick={close}>Cancel</Button>,
        short && !totalShort && canConsolidate ? (
          <Button key="consolidate" variant="default" onClick={() => approve.mutate({ consolidate: true })} loading={approve.isPending} disabled={!wid}>
            Approve &amp; create transfers
          </Button>
        ) : null,
        <Button key="approve" variant="primary" onClick={() => approve.mutate({})} loading={approve.isPending} disabled={!wid}>
          {short ? 'Approve anyway' : 'Approve'}
        </Button>,
      ]}
    >
      {isPrepaid && (
        <Alert
          type={advPaid > 0 ? 'success' : advPending > 0 ? 'warning' : 'info'}
          style={{ marginBottom: 12 }}
          title={
            <span>
              Prepaid order{' '}
              {cdStatus === 'LOCKED'
                ? <Tag tone="green" style={{ margin: 0 }}>Advance CD locked</Tag>
                : cdStatus === 'ESTIMATED'
                  ? <Tag tone="gold" style={{ margin: 0 }}>Advance CD estimated</Tag>
                  : null}
            </span>
          }
          description={
            <div style={{ fontSize: 13 }}>
              {(cdStatus === 'ESTIMATED' || cdStatus === 'LOCKED') && advanceCd?.netPayable != null && (
                <div>Advance cash discount {cdStatus === 'LOCKED' ? 'locked' : '(estimated)'} — net payable <b>{inr2(advanceCd.netPayable)}</b>{advanceCd?.original?.grand != null && <> (was {inr2(advanceCd.original.grand)})</>}.</div>
              )}
              {advPaid > 0
                ? <div style={{ color: '#389e0d' }}>✓ Advance {inr2(advPaid)} received &amp; verified.</div>
                : advPending > 0
                  ? <div style={{ color: '#d46b08' }}>Advance {inr2(advPending)} submitted — pending Accounts verification.</div>
                  : <div style={{ color: '#8c8c8c' }}>No advance payment received yet.</div>}
              <div style={{ color: '#8c8c8c', fontSize: 12, marginTop: 2 }}>
                Money is committed — consider prioritising fulfilment. (The discount is locked by Accounts later, before billing; approval doesn’t change it.)
              </div>
            </div>
          }
        />
      )}

      <Alert type="info" style={{ marginBottom: 12 }}
        title="Assign the fulfillment warehouse"
        description="The warehouse you pick packs & dispatches this order (its manager sees it in their queue). Pick one that holds the stock — or approve and create transfers to bring stock in; anything still short is backordered." />

      <Typography.Text strong>Fulfillment warehouse <span style={{ color: '#ff4d4f' }}>*</span></Typography.Text>
      <Select showSearch optionFilterProp="text"
        placeholder="Select warehouse" style={{ width: '100%', margin: '4px 0 10px' }}
        value={wid} onChange={setWid} options={whOptions} />

      {wid && plan && (
        !plan.fullyAvailableHere
          ? <Tag tone="gold" style={{ marginBottom: 8 }}>Short on some items here</Tag>
          : freeCovers
            ? <Tag tone="green" style={{ marginBottom: 8 }}>✓ This warehouse fully covers the order</Tag>
            : <Tag tone="blue" style={{ marginBottom: 8 }}>Physically covers — but some stock is committed to other orders</Tag>
      )}
      {wid && short && totalShort && (
        <Alert type="warning" style={{ marginBottom: 8 }}
          title="Stock is short even across all warehouses"
          description="Transfers can't fully cover this order. Approve to fulfil what's available — the remainder is backordered and releases when stock is replenished (e.g. a production run)." />
      )}
      {wid && committedHere && (
        <Alert type="info" style={{ marginBottom: 8 }}
          title="Some stock here is already committed to other orders"
          description="The table shows physical stock here, how much is already spoken-for by other active orders assigned to this warehouse, and what's free for this order. Advisory only — nothing is reserved. Decide as you see fit: bring stock in, backorder, or prioritise another order." />
      )}

      {wid && (
        <Table size="small" loading={planLoading} pagination={false} rowKey="orderItemId" dataSource={lines}
          locale={{ emptyText: 'No lines' }}
          columns={[
            { title: 'Product', dataIndex: 'productName', render: (v: any, l: any) => (
              <div>
                <div>{v ?? l.productId}{l.variantLabel ? <span style={{ color: '#8c8c8c' }}> · {l.variantLabel}</span> : ''}</div>
                {l.sku && <div style={{ fontSize: 11, color: '#8c8c8c' }}>{l.sku}</div>}
              </div>
            ) },
            { title: 'Ordered', dataIndex: 'pendingQty', align: 'right' as const, render: (v: any) => fmtQty(v) },
            // When other orders have committed stock here, show physical → committed → free (advisory);
            // otherwise keep the plain physical view.
            ...(committedHere
              ? [
                  { title: 'Physical here', dataIndex: 'availableHere', align: 'right' as const, render: (v: any) => fmtQty(v) },
                  { title: 'Committed', dataIndex: 'committedElsewhere', align: 'right' as const, render: (v: any) => (Number(v) > 0 ? <span style={{ color: '#d46b08' }}>−{fmtQty(v)}</span> : '—') },
                  { title: 'Free here', dataIndex: 'freeHere', align: 'right' as const, render: (v: any, l: any) => <b style={{ color: Number(v) < Number(l.pendingQty) ? '#d4380d' : '#389e0d' }}>{fmtQty(v)}</b> },
                ]
              : [
                  { title: 'Available here', dataIndex: 'availableHere', align: 'right' as const, render: (v: any, l: any) => <span style={{ color: l.shortHere > 0 ? '#d4380d' : undefined }}>{fmtQty(v)}</span> },
                  { title: 'Short', dataIndex: 'shortHere', align: 'right' as const, render: (v: any) => (v > 0 ? <b style={{ color: '#d4380d' }}>{fmtQty(v)}</b> : '—') },
                ]),
            { title: 'Total (all WH)', dataIndex: 'availableTotal', align: 'right' as const, render: (v: any, l: any) => <span style={{ color: Number(v) < Number(l.pendingQty) ? '#d4380d' : undefined }}>{fmtQty(v)}</span> },
          ]} />
      )}

      <div style={{ marginTop: 10 }}>
        <Typography.Text type="secondary">Notes (optional)</Typography.Text>
        <Input.TextArea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Approval notes…" style={{ marginTop: 4 }} />
      </div>
    </Modal>
  );
}
